import * as React from "react";

import { Stakeholder } from "../../domain";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FullScreenDialog,
    RelodeTheme,
    useDeviceSize,
    DialogActions,
} from "../../components";
import { makeStyles, createStyles } from "@material-ui/core";
import {
    InterviewNotificationEmailForm,
    InterviewNotificationEmailPreview,
} from "./notification";
import { MessageRecipient } from "../messages";
import { CandidateNotificationErrorType } from "./types";

const styles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            contentContainer: {
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                backgroundColor: theme.relode.colors.pageSecondaryBackground,
            },
        }),
    { name: "CandidateInterviewNotificationDialog" }
);

interface CandidateInterviewNotificationDialogProps {
    candidateUserId: number;
    candidateRoleName: string;
    candidateFullName: string;
    candidateFirstName: string;
    jobName: string;
    jobId: number;
    companyName: string;
    companyBackground: string;
    interviewId: number;
    interviewStartDateTime: string;
    interviewTimeZoneId: number;
    interviewDurationHours: number;
    interviewDurationMinutes: number;
    interviewDetails: string;
    candidateManagedBy?: Stakeholder;
    jobJobManager: Stakeholder;
    onClose: () => void;
    handleSendManualInterviewNotification: (
        interviewId: number,
        subject: string,
        recipientUserIds: number[]
    ) => Promise<boolean>;
    isCandidateLoading: boolean;
}

export const CandidateInterviewNotificationDialog = (
    props: CandidateInterviewNotificationDialogProps
) => {
    const {
        candidateFullName,
        candidateFirstName,
        candidateUserId,
        candidateRoleName,
        candidateManagedBy,
        jobName,
        jobId,
        companyName,
        interviewId,
        interviewStartDateTime,
        interviewTimeZoneId,
        interviewDurationHours,
        interviewDurationMinutes,
        interviewDetails,
        companyBackground,
        jobJobManager,
        isCandidateLoading,
        handleSendManualInterviewNotification,
        onClose,
    } = props;
    const classes = styles({});
    const device = useDeviceSize();

    const [
        notificationSubject,
        setNotificationSubject,
    ] = React.useState<string>("");
    const [
        inputError,
        setInputError,
    ] = React.useState<CandidateNotificationErrorType>({
        recipientsError: null,
        subjectError: null,
    });
    const [selectedRecipients, setSelectedRecipients] = React.useState<
        MessageRecipient[]
    >([
        {
            userId: candidateUserId,
            displayName: candidateFullName,
            roleName: candidateRoleName,
        },
    ]);

    const handleSetNotificationSubject = (subject: string) => {
        if (subject?.length <= 0) {
            setInputError({
                subjectError:
                    "please enter a subject line for interview reminder",
                recipientsError: inputError.recipientsError,
            });
        } else {
            setInputError({
                subjectError: null,
                recipientsError: inputError.recipientsError,
            });
        }
        setNotificationSubject(subject);
    };

    const handleSetSelectedRecipients = (recipients: MessageRecipient[]) => {
        if (recipients?.length <= 0) {
            setInputError({
                recipientsError: "please select at least one recipient",
                subjectError: inputError.subjectError,
            });
        } else {
            setInputError({
                recipientsError: null,
                subjectError: inputError.subjectError,
            });
        }
        setSelectedRecipients(recipients);
    };

    const handleSendReminder = async () => {
        if (!inputError.recipientsError && !inputError.subjectError) {
            const recipientIds: number[] = [];
            selectedRecipients.forEach(r => recipientIds.push(r.userId));

            const result = await handleSendManualInterviewNotification(
                interviewId,
                notificationSubject.trim(),
                recipientIds
            );

            if (result) {
                onClose();
            }
        }
    };

    if (device === "mobile")
        return (
            <Dialog open>
                <DialogTitle title="send reminder" onClose={onClose} />
                <DialogContent>
                    <InterviewNotificationEmailForm
                        candidateUserId={candidateUserId}
                        jobId={jobId}
                        interviewStartDateTime={interviewStartDateTime}
                        interviewTimeZoneId={interviewTimeZoneId}
                        notificationSubject={notificationSubject}
                        setNotificationSubject={handleSetNotificationSubject}
                        selectedRecipients={selectedRecipients}
                        setSelectedRecipients={handleSetSelectedRecipients}
                        inputError={inputError}
                        initialRecipients={[
                            {
                                userId: candidateUserId,
                                displayName: candidateFullName,
                                roleName: candidateRoleName,
                            },
                        ]}
                        isMobile={device === "mobile"}
                    />
                </DialogContent>
                <DialogActions
                    submit={{
                        label: "send",
                        onSubmit: handleSendReminder,
                        disabled:
                            !!inputError.recipientsError ||
                            !!inputError.subjectError ||
                            isCandidateLoading,
                    }}
                />
            </Dialog>
        );
    else
        return (
            <FullScreenDialog
                open
                title={`${candidateFullName} - ${jobName}`}
                onClose={onClose}
                rightContent={
                    <Button
                        color="primary"
                        size="small"
                        variant="contained"
                        onClick={handleSendReminder}
                        disabled={
                            !!inputError.recipientsError ||
                            !!inputError.subjectError ||
                            isCandidateLoading
                        }
                    >
                        send reminder
                    </Button>
                }
                isBusy={isCandidateLoading}
            >
                <div className={classes.contentContainer}>
                    <InterviewNotificationEmailPreview
                        candidateFirstName={candidateFirstName}
                        jobName={jobName}
                        companyName={companyName}
                        companyBackground={companyBackground}
                        interviewStartDateTime={interviewStartDateTime}
                        interviewTimeZoneId={interviewTimeZoneId}
                        interviewDurationHours={interviewDurationHours}
                        interviewDurationMinutes={interviewDurationMinutes}
                        interviewDetails={interviewDetails}
                        candidateManagedBy={candidateManagedBy}
                        jobJobManager={jobJobManager}
                    />
                    <InterviewNotificationEmailForm
                        candidateUserId={candidateUserId}
                        jobId={jobId}
                        interviewStartDateTime={interviewStartDateTime}
                        interviewTimeZoneId={interviewTimeZoneId}
                        notificationSubject={notificationSubject}
                        setNotificationSubject={handleSetNotificationSubject}
                        selectedRecipients={selectedRecipients}
                        setSelectedRecipients={handleSetSelectedRecipients}
                        inputError={inputError}
                        initialRecipients={[
                            {
                                userId: candidateUserId,
                                displayName: candidateFullName,
                                roleName: candidateRoleName,
                            },
                        ]}
                    />
                </div>
            </FullScreenDialog>
        );
};
